import React, {useEffect, useRef, useState} from 'react';
import {View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {ThemeProvider} from '@rneui/themed';
import {ErrorMessage as FormikErrorMessage, Formik} from 'formik';
import _, { values } from 'lodash';
import {observer} from 'mobx-react-lite';
import * as Yup from 'yup';

import AcceptOnlineOrderingTCs from '../../../components/rewards/AcceptOnlineOrderingTCs';
import AskToVerify from '../../../components/rewards/AskToVerify';
import OptIn from '../../../components/rewards/OptIn';
import ShopLoyaltyEnabled from '../../../components/rewards/ShopLoyaltyEnabled';
import Button from '../../../components/themed/Button';
import Text from '../../../components/themed/Text';
import TextInput from '../../../components/themed/TextInput';
import ValidationMessage from '../../../components/themed/ValidationMessage';
import AddressEntry from '../../../components/ui-controls/address/AddressEntry';
import DateOfBirthInput from '../../../components/ui-controls/DateOfBirthInput';
import ContentView from '../../../components/ui-controls/layout/ContentView';
import Modal from '../../../components/ui-controls/Modal';
import Checkbox from '../../../components/ui-controls/web/CheckBox';
import useIsLoggedIn from '../../../hooks/useIsLoggedIn';
import {AppRoute} from '../../../models/AppRoute';
import {Address, User} from '../../../models/mfl';
import {
  AccountScreenAction,
  RouteNavigatorScreenProps,
} from '../../../models/navigationTypes';
import {useStores} from '../../../stores/use-stores';
import rewardsTheme from '../../../theme/rewardsTheme';
import apiService from '../../../transport/apiService';
import {Analytics} from '../../../utils/analytics/analytics';
import {
  EventName,
  EventScreenName,
  EventSourceType,
} from '../../../utils/analytics/events';
import TogglePasswordVisibleIconButton from '../components/TogglePasswordVisibleIconButton';

import {styles} from './styles';
import {
  checkPhoneNumberInUseSchema,
  CreateAccountSchema,
  passwordRequired,
} from './validationSchemaLoyaltyEnabled';

function InputLabel({
  children,
  required,
}: {
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <Text variant="body2" style={{marginBottom: 5}}>
      {children}
      {!!required && (
        <Text variant="caption" style={{verticalAlign: 'super'}}>
          {' '}
          *
        </Text>
      )}
    </Text>
  );
}

const ErrorMessage = ({name}: {name: string}) => (
  // @ts-ignore
  <FormikErrorMessage component={ValidationMessage} name={name} />
);

const AccountCreateScreen = observer(
  ({
    route,
    navigation,
  }: RouteNavigatorScreenProps<
    | AppRoute.ACCOUNT_CREATE
    | AppRoute.ACCOUNT_DETAIL
    | AppRoute.ACCOUNT_CHANGE_PASSWORD
    | AppRoute.ACCOUNT_COLLECT_MISSING_DETAILS
  >) => {
    const {userStore} = useStores();
    const isLoggedIn = useIsLoggedIn();

    const [isPasswordVisible, setPasswordVisible] = useState(false);
    const [isConfirmPasswordVisible, setConfirmPasswordVisible] =
      useState(false);
    const [isWeakPasswordCheckboxVisible, setWeakPasswordCheckboxVisible] =
      useState(false);
    const [isSubmitting, setSubmitting] = useState(false);
    const [isPasswordUpdatedModalVisible, setIsPasswordUpdatedModalVisible] =
      useState(false);

    const action = route.params?.action;
    const email = route.params?.email;

    const isCreate = action === AccountScreenAction.CREATE;
    const isUpdate = action === AccountScreenAction.UPDATE;
    const isCreateOrUpdate = isCreate || isUpdate;
    const isCreateOrChangePassword = isCreate ||
      action === AccountScreenAction.CHANGE_PASSWORD;
    const isCollectMissingDetails = action === AccountScreenAction.COLLECT_MISSING_DETAILS;

    useEffect(() => {
      setWeakPasswordCheckboxVisible(userStore.showWeakPasswordCheckbox);
    }, [userStore.showWeakPasswordCheckbox]);

    const handleTogglePasswordVisible = () =>
      setPasswordVisible(!isPasswordVisible);

    const handleToggleConfirmPasswordVisible = () =>
      setConfirmPasswordVisible(!isConfirmPasswordVisible);

    const submitSuccessCallback = () => {
      setSubmitting(false);
      if (password.current) {
        // @ts-ignore
        password.current.clear();
      }
      if (isCreateOrChangePassword) {
        setIsPasswordUpdatedModalVisible(true);
        return;
      }
      postSubmitSuccess();
    };

    const postSubmitSuccess = () => {
      setIsPasswordUpdatedModalVisible(false);
      userStore.afterUpdateUser();
    }

    const submitErrorCallback = () => setSubmitting(false);

    const onSubmit = async (values: CreateAccountValues) => {
      const showSuccessToast = !isCreateOrChangePassword;
      setSubmitting(true);
      if (isLoggedIn) {
        const shouldVerifyMobileNumber = userStore.phoneNumber !== values.phoneNumber;

        // Ensure billingAddress.lines is always an array
        const userValues: User = {
          ...values,
          billingAddress: values.billingAddress ? {
            ...values.billingAddress,
            lines: values.billingAddress.lines || [],
          } : undefined
        };
      
        if (isUpdate && shouldVerifyMobileNumber) {
        userStore.updateUserFormValues(_.cloneDeep(values));
          await apiService.sendMobileVerificationCode({
            phoneNumber: values.phoneNumber,
          });
          navigation.navigate(AppRoute.ACCOUNT_PHONE_VERIFICATION,
            {
              action,
            }
          );
        } else {
        await userStore.updateUserInfo(
          action,
          userValues,
          submitSuccessCallback,
          submitErrorCallback,
          {
            showSuccessToast
          }
        );
      }
      } else {
        // TODO: turn this back on, but now sign up is a 2 step process
        // So we probably want a second log for when the sign up is complete, and this
        // will be when sign up has been initiated. Ask the business what they want.
        //   Analytics.logEvent({
        //     eventName: EventName.SignUp,
        //     parameters: {
        //       screen_name: EventScreenName.Account,
        //       source_type: EventSourceType.Button,
        //       method: 'email',
        //     },
        //   });
        let userValues: any = _.cloneDeep(values);
        delete userValues.passwordValidationBypass;
        delete userValues.optInRewards;

        let passwordIsOkResponse: any;
        try {
          passwordIsOkResponse = await apiService.checkPasswordStrength(
            userValues,
          );
        } catch (error) {
          passwordIsOkResponse = null;
        }

        const passwordIsOk = !!passwordIsOkResponse?.password_is_ok;

        if (!passwordIsOk && !values.passwordValidationBypass) {
          setWeakPasswordCheckboxVisible(true);
          setSubmitting(false);
          return;
        }

        userStore.updateUserFormValues(_.cloneDeep(values));
        await apiService.sendMobileVerificationCode({
          phoneNumber: values.phoneNumber,
        });

        setSubmitting(false);
        navigation.navigate(AppRoute.ACCOUNT_PHONE_VERIFICATION);
      }
      setSubmitting(false);
    };

    const password = useRef<HTMLInputElement>(null);
    const confirmPassword = useRef<HTMLInputElement>(null);

    const validationSchema = isCreateOrChangePassword
      ? isCreate
        ? CreateAccountSchema.concat(passwordRequired).concat(
            checkPhoneNumberInUseSchema,
          )
        : CreateAccountSchema.concat(passwordRequired)
      : CreateAccountSchema;

    type CreateAccountValues = Yup.InferType<typeof validationSchema>;

    const defaultValues: CreateAccountValues = {
      firstName: userStore.firstName,
      lastName: userStore.lastName,
      email: email || userStore.emailAddress,
      dateOfBirth: userStore.dateOfBirth,
      phoneNumber: userStore.phoneNumber,
      billingAddress: userStore.billingAddress || {
        lines: [],
        locality: '',
        administrative_area: '',
        postal_code: '',
      },
      password: '',
      confirmPassword: '',
      passwordValidationBypass: false,
      optInRewards: false, // The Opt-in checkbox must be unselected to begin, we cannot preselect this option.
    };

    const disableField = {
      firstName: isCollectMissingDetails && userStore.firstName.trim().length > 0,
      lastName: isCollectMissingDetails && userStore.lastName.trim().length > 0,
      dateOfBirth: isCollectMissingDetails && !!userStore.dateOfBirth && !isNaN(userStore.dateOfBirth?.getTime()),
      phoneNumber: isCollectMissingDetails && userStore.phoneNumber.trim().length > 0,
      billingAddress: isCollectMissingDetails && userStore.billingAddress && userStore.billingAddress.lines.length !== 0,
      mobileNumber: isCollectMissingDetails && userStore.phoneNumber.trim().length > 0,
    }

    return (
      <ThemeProvider theme={rewardsTheme}>
        <KeyboardAwareScrollView
          style={{backgroundColor: 'white'}}
          keyboardShouldPersistTaps="always">
          <Formik<CreateAccountValues>
            initialValues={defaultValues}
            onSubmit={onSubmit}
            validationSchema={validationSchema}>
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              initialValues,
              setFieldValue,
              setFieldError,
              setFieldTouched,
              touched,
              values,
            }) => {
              const handleSubmitPress = () => {
                setFieldTouched('billingAddress', true);
                handleSubmit();
              };
              const renderErrorMessageWhenTouchedOrPlaceholder = (
                name: keyof CreateAccountValues,
              ) => (
                <ValidationMessage>
                  {touched[name] && errors[name]
                    ? (errors[name] as string)
                    : ' '}
                </ValidationMessage>
              );

              const handleAddressChange = (value: Address) => {
                setFieldValue('billingAddress', value);
              };

              return (
                <ContentView>
                  <View style={{gap: 8}}>
                    {!!(isCreateOrUpdate || isCollectMissingDetails) && (
                      <>
                        <Text style={{marginVertical: 16}}>
                          {
                            isCollectMissingDetails 
                            ? "We seem to be missing some details from your account. Please verify and fill out the details below to continue."
                            : "Fill out your details below to create an account to enjoy online shopping and member rewards"
                          }
                        </Text>

                        <TextInput
                          accessibilityLabel="First name"
                          autoCapitalize="words"
                          autoComplete="name-given"
                          errorMessageWhenTouched={errors.firstName}
                          keyboardType="default"
                          label={<InputLabel required>First name</InputLabel>}
                          maxLength={50}
                          onBlur={handleBlur('firstName')}
                          onChangeText={handleChange('firstName')}
                          touched={touched.firstName}
                          value={values.firstName}
                          disabled={disableField.firstName}
                        />
                        <TextInput
                          accessibilityLabel="Last name"
                          autoCapitalize="words"
                          autoComplete="name-family"
                          defaultValue={initialValues.lastName}
                          errorMessageWhenTouched={errors.lastName}
                          keyboardType="default"
                          label={<InputLabel required>Last name</InputLabel>}
                          maxLength={50}
                          onBlur={handleBlur('lastName')}
                          onChangeText={handleChange('lastName')}
                          touched={touched.lastName}
                          value={values.lastName}
                          disabled={disableField.lastName}
                        />
                        <TextInput
                          accessibilityLabel="Email address"
                          autoCapitalize="none"
                          autoComplete="email"
                          defaultValue={initialValues.email}
                          disabled={true}
                          editable={false}
                          errorMessageWhenTouched={errors.email}
                          keyboardType="email-address"
                          label={
                            <InputLabel required>Email address</InputLabel>
                          }
                          maxLength={100}
                          onBlur={handleBlur('email')}
                          onChangeText={handleChange('email')}
                          touched={touched.email}
                        />
                      </>
                    )}
                    {!!isCreateOrChangePassword && (
                      <>
                        <TextInput
                          autoCapitalize="none"
                          defaultValue={initialValues.password}
                          label={<InputLabel required>Password</InputLabel>}
                          onBlur={handleBlur('password')}
                          onChangeText={handleChange('password')}
                          ref={password}
                          secureTextEntry={!isPasswordVisible}
                          touched={touched.password}
                          errorMessage={
                            errors.password ||
                            'Please enter a strong password with a mix of letters, numbers and symbols'
                          }
                          errorStyle={
                            errors.password
                              ? {}
                              : {color: '#6F6F6F', fontStyle: 'italic'}
                          }
                          errorMessageWhenTouched={
                            isWeakPasswordCheckboxVisible
                              ? undefined
                              : errors.password
                          }
                          rightIconContainerStyle={{
                            position: 'absolute',
                            right: 10,
                          }}
                          rightIcon={
                            <TogglePasswordVisibleIconButton
                              isPasswordVisible={isPasswordVisible}
                              onPress={handleTogglePasswordVisible}
                            />
                          }
                        />
                        {!!isWeakPasswordCheckboxVisible && (
                          <View
                            style={[
                              styles.checkboxContainer,
                              styles.formElement,
                            ]}>
                            <Checkbox
                              boxType="square"
                              checked={values.passwordValidationBypass}
                              onChange={() =>
                                setFieldValue(
                                  'passwordValidationBypass',
                                  !values.passwordValidationBypass,
                                )
                              }
                              style={styles.checkbox}
                            />
                            <Text style={styles.compromisedPasswordText}>
                              This password has been found in a list of
                              passwords that was leaked from other websites. By
                              ticking this box you acknowledge that you wish to
                              continue with this password.
                            </Text>
                          </View>
                        )}
                        <TextInput
                          autoCapitalize="none"
                          defaultValue={initialValues.confirmPassword}
                          label={
                            <InputLabel required>Confirm password</InputLabel>
                          }
                          onBlur={handleBlur('confirmPassword')}
                          onChangeText={handleChange('confirmPassword')}
                          ref={confirmPassword}
                          secureTextEntry={!isConfirmPasswordVisible}
                          touched={touched.confirmPassword}
                          errorMessageWhenTouched={
                            isWeakPasswordCheckboxVisible
                              ? undefined
                              : errors.confirmPassword
                          }
                          rightIconContainerStyle={{
                            position: 'absolute',
                            right: 10,
                          }}
                          rightIcon={
                            <TogglePasswordVisibleIconButton
                              isPasswordVisible={isConfirmPasswordVisible}
                              onPress={handleToggleConfirmPasswordVisible}
                            />
                          }
                        />
                      </>
                    )}

                    {!!(isCreateOrUpdate || isCollectMissingDetails) && (
                      <>
                        <View style={{marginTop: 8}}>
                          <Text variant="body2" style={{marginBottom: 8}}>
                            <InputLabel required>Billing Address</InputLabel>
                          </Text>
                          <AddressEntry
                            onChange={handleAddressChange}
                            address={values.billingAddress}
                            placeholder=""
                            disabled={disableField.billingAddress}
                          />
                          <View style={{minHeight: 30}}>
                            {!values.billingAddress &&
                            !!errors.billingAddress &&
                            !!touched.billingAddress ? (
                              <ValidationMessage>
                                This is a required field
                              </ValidationMessage>
                            ) : (
                              <ValidationMessage>
                                <ErrorMessage name="billingAddress.lines" />
                                <ErrorMessage name="billingAddress.locality" />
                                <ErrorMessage name="billingAddress.administrative_area" />
                                <ErrorMessage name="billingAddress.postal_code" />
                              </ValidationMessage>
                            )}
                          </View>
                        </View>
                        <View style={styles.dateOfBirthContainer}>
                          <InputLabel>Date of Birth</InputLabel>
                          <DateOfBirthInput
                            initialDate={defaultValues.dateOfBirth}
                            disabled={isUpdate || disableField.dateOfBirth}
                            onChange={date => {
                              setFieldValue('dateOfBirth', date, false);
                              setFieldError('dateOfBirth', undefined);
                              setFieldTouched('dateOfBirth', true, false);
                            }}
                            onInvalidDate={date => {
                              setFieldValue('dateOfBirth', date, false);
                              setFieldError('dateOfBirth', 'Invalid date');
                              setFieldTouched('dateOfBirth', true, false);
                            }}
                          />
                          {renderErrorMessageWhenTouchedOrPlaceholder(
                            'dateOfBirth',
                          )}
                        </View>

                        {!!isCreate && (
                          <ShopLoyaltyEnabled>
                            <OptIn
                              checked={!!values.optInRewards}
                              onChange={value => {
                                setFieldValue(
                                  'optInRewards',
                                  !values.optInRewards,
                                );
                              }}
                            />
                          </ShopLoyaltyEnabled>
                        )}
                        <TextInput
                          accessibilityLabel="Mobile number"
                          autoCapitalize="none"
                          autoComplete="tel"
                          defaultValue={initialValues.phoneNumber}
                          errorMessageWhenTouched={errors.phoneNumber}
                          keyboardType="phone-pad"
                          label={
                            <InputLabel required>Mobile number</InputLabel>
                          }
                          maxLength={20}
                          onBlur={handleBlur('phoneNumber')}
                          onChangeText={handleChange('phoneNumber')}
                          touched={touched.phoneNumber}
                          disabled={disableField.mobileNumber}
                        />
                        {!!isCreate && <AcceptOnlineOrderingTCs />}
                        {!!(isCreate || !disableField.mobileNumber) && <AskToVerify action={action} />}
                      </>
                    )}
                    <View style={{alignItems: 'flex-end', marginTop: 16}}>
                      <Button
                        width={194}
                        title={isLoggedIn ? 'Save' : 'Next'}
                        onPress={handleSubmitPress}
                        disabled={isSubmitting}
                        loading={isSubmitting}
                      />
                    </View>
                  </View>
                </ContentView>
              );
            }}
          </Formik>
           <Modal isVisible={isPasswordUpdatedModalVisible} title="Password updated. Please log in with your new password.">
            <Button title="Continue" onPress={postSubmitSuccess} width={150} style={{marginTop: 16}} />
          </Modal>
        </KeyboardAwareScrollView>
      </ThemeProvider>
    );
  },
);

export default AccountCreateScreen;
